import { useRef } from "react";

const HANDLE = "absolute rounded-full border-2 border-white shadow";

function bbox(quad) {
  const xs = quad.map((p) => p[0]); const ys = quad.map((p) => p[1]);
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

export default function ArtifactCanvas({ tpl, layout, bgUrl, selected, onSelect, onChange, displayWidth = 560 }) {
  const scale = displayWidth / tpl.width;
  const drag = useRef(null);

  const start = (e, r, mode) => {
    e.stopPropagation(); e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    onSelect(r.id);
    drag.current = { mx: e.clientX, my: e.clientY, r: JSON.parse(JSON.stringify(r)), mode };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.mx) / scale; const dy = (e.clientY - d.my) / scale;
    const r = JSON.parse(JSON.stringify(d.r));
    const W = tpl.width; const H = tpl.height;
    if (d.mode === "move") {
      r.x = Math.round(Math.max(0, Math.min(W - r.w, d.r.x + dx)));
      r.y = Math.round(Math.max(0, Math.min(H - r.h, d.r.y + dy)));
      if (r.quad) r.quad = d.r.quad.map(([qx, qy]) => [Math.round(qx + (r.x - d.r.x)), Math.round(qy + (r.y - d.r.y))]);
    } else if (d.mode === "resize") {
      r.w = Math.round(Math.max(20, Math.min(W - r.x, d.r.w + dx)));
      r.h = Math.round(Math.max(20, Math.min(H - r.y, d.r.h + dy)));
    } else if (d.mode.startsWith("q")) {
      const i = Number(d.mode.slice(1));
      r.quad[i] = [Math.round(Math.max(0, Math.min(W, d.r.quad[i][0] + dx))), Math.round(Math.max(0, Math.min(H, d.r.quad[i][1] + dy)))];
      Object.assign(r, bbox(r.quad));
    }
    onChange(r);
  };
  const end = () => { drag.current = null; };

  return (
    <div data-testid="artifact-canvas" className="relative shrink-0 select-none overflow-hidden rounded border border-[#243144] bg-black"
      style={{ width: displayWidth, height: tpl.height * scale, touchAction: "none" }} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
      onPointerDown={() => onSelect(null)}>
      {bgUrl && <img src={bgUrl} alt="artwork" className="pointer-events-none absolute inset-0 h-full w-full" draggable={false} />}
      {layout.regions.filter((r) => r.quad).map((r) => (
        <svg key={`${r.id}-poly`} className="pointer-events-none absolute inset-0" width={displayWidth} height={tpl.height * scale}>
          <polygon points={r.quad.map(([x, y]) => `${x * scale},${y * scale}`).join(" ")} fill="rgba(56,189,248,.18)" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />
        </svg>
      ))}
      {layout.regions.map((r) => {
        const sel = selected === r.id;
        const color = r.kind === "photo" ? "56,189,248" : "251,191,36";
        return (
          <div key={r.id} data-testid={`artifact-region-${r.id}`} onPointerDown={(e) => start(e, r, "move")} onPointerMove={move} onPointerUp={end}
            className="absolute cursor-move" style={{ left: r.x * scale, top: r.y * scale, width: r.w * scale, height: r.h * scale,
              border: `${sel ? 2 : 1}px ${r.quad ? "dotted" : "solid"} rgb(${color})`, background: r.quad ? "transparent" : `rgba(${color},.14)` }}>
            <span className="pointer-events-none absolute left-0 top-0 -translate-y-full whitespace-nowrap bg-black/70 px-1 text-[9px] text-white">{r.id}</span>
            {!r.quad && (
              <span data-testid={`artifact-resize-${r.id}`} onPointerDown={(e) => start(e, r, "resize")} onPointerMove={move} onPointerUp={end}
                className={`${HANDLE} -bottom-1.5 -right-1.5 h-3 w-3 cursor-nwse-resize bg-sky-500`} />
            )}
          </div>
        );
      })}
      {layout.regions.filter((r) => r.quad).map((r) => r.quad.map(([x, y], i) => (
        <span key={`${r.id}-q${i}`} data-testid={`artifact-corner-${i}`} onPointerDown={(e) => start(e, r, `q${i}`)} onPointerMove={move} onPointerUp={end}
          className={`${HANDLE} h-4 w-4 cursor-crosshair bg-pink-500`} style={{ left: x * scale - 8, top: y * scale - 8 }} title={["top-left", "top-right", "bottom-right", "bottom-left"][i]} />
      )))}
    </div>
  );
}
